// ---------------------------------------------------------------------------
// Route guards.
//
// RequireAuth  - you must be signed in.
// RequirePermission - you must hold a specific permission.
//
// These stop a person reaching a screen by typing its address, which is the
// obvious hole left if you only hide menu items. They are still not the real
// security boundary: the API checks the same permission again on every
// request, so even a modified browser gets nothing.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Spinner } from '../components/common/Spinner';
import { landingPathFor } from './navigation';

/// Shown for the moment it takes to ask the server whether an existing cookie
/// is still valid. Without it, a signed-in employee refreshing the page would
/// see the login screen flash before being let back in.
function SessionCheck() {
  return (
    <div className="flex h-screen items-center justify-center bg-surface-page">
      <div className="flex flex-col items-center gap-3 text-butcher-green">
        <Spinner size="lg" label="Checking your session" />
        <p className="text-sm text-ink-muted">Yaghi Butcher POS</p>
      </div>
    </div>
  );
}

export function RequireAuth({ children }: { children?: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const location = useLocation();

  if (status === 'checking') return <SessionCheck />;

  if (status !== 'authenticated') {
    // Remember where they were headed so they land there after signing in.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children ?? <Outlet />}</>;
}

export function RequirePermission({ permission, children }: { permission: string; children?: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const has = useAuthStore((state) => state.permissions.has(permission));
  const location = useLocation();

  if (status === 'checking') return <SessionCheck />;

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!has) {
    // Sent to a page that explains the refusal, carrying which permission was
    // needed so the employee can tell the owner exactly what to grant.
    return <Navigate to="/unauthorized" replace state={{ permission, from: location.pathname }} />;
  }

  return <>{children ?? <Outlet />}</>;
}

/// The login page itself: someone already signed in should not see it again.
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const permissions = useAuthStore((state) => state.permissions);

  if (status === 'checking') return <SessionCheck />;

  if (status === 'authenticated') {
    // Each person is sent to the first screen they are actually allowed to
    // open - a cashier has no dashboard, so they land on the POS instead.
    return <Navigate to={landingPathFor(permissions)} replace />;
  }

  return <>{children}</>;
}
